/**
 * A map's shot made into its tiles (ADR-0223, `MapTiles`): the frames the game's own client takes over the map proper
 * (`?mapshot`) cut into the top level at the art's density, then halved level by level down to one tile. Pure (RGBA
 * buffers): the client's `app/mapshot.ts` shoots for the admin app's Maps page, `mapkit tiles` cuts a playtest's frames.
 */
import type { MapTiles } from './api';

/** A tile's side in px (`MapTiles.size`). */
export const MAP_TILE = 256;

/**
 * The maps the Maps page shoots, and how the game's client opens each with nobody else on the map proper (`?mapshot`): a
 * battle royale's warm-up held on its island, Zombie Nights' first day, the range.
 */
export const MAP_SHOTS: Record<string, string> = {
  village: 'royale&bots=0&lobby=900&frost=900&balloon=900&mapshot',
  bramblewick: 'royale&map=bramblewick&bots=0&lobby=900&frost=900&balloon=900&mapshot',
  'zombie-farm': 'zombies&map=zombie-farm&mapshot',
  'willow-bend': 'zombies&map=willow-bend&mapshot',
  'twin-hollows': 'zombies&map=twin-hollows&mapshot',
  range: 'range&mapshot',
};

/**
 * The Maps page and the game's client in its iframe (`?mapshot`, `postMessage`): the client says it is `ready` once its
 * map is built (the map and the version it draws: its file's git blob sha, baked into its build), the page answers
 * `shoot`, and the client says how far it is (`frames` shot, `tiles` made: `done` of `of`), then hands over the `shot`
 * (its manifest, each tile's lossless WebP by sha) or says it `failed`. `B`: a Blob (no DOM here).
 */
export type MapShotMessage<B = unknown> =
  | { mapshot: 'ready'; map: string; version: string }
  | { mapshot: 'shoot' }
  | { mapshot: 'frames' | 'tiles'; done: number; of: number }
  | { mapshot: 'shot'; manifest: MapTiles; files: [string, B][] }
  | { mapshot: 'failed'; error: string };

/** A shot's shape: the map proper in px at its density, its top level's tiles across and down, its levels. */
export interface ShotShape {
  density: number;
  size: number;
  /** The map proper in px. */
  pw: number;
  ph: number;
  nx: number;
  ny: number;
  levels: number;
}

/** The shape of a shot of `width` × `height` world px at `density` px a world px. */
export function shotShape(width: number, height: number, density: number, size = MAP_TILE): ShotShape {
  const [pw, ph] = [Math.round(width * density), Math.round(height * density)];
  const [nx, ny] = [Math.ceil(pw / size), Math.ceil(ph / size)];
  return { density, size, pw, ph, nx, ny, levels: 33 - Math.clz32(Math.max(nx, ny) - 1) };
}

/**
 * A frame's tiles into the top `level` (keys `${x}/${y}`): each whole in the frame and not there yet, cut short at the
 * map's edge (the rest stays clear). `px`: the frame's RGBA, `w` × `h`; (x, y): the world point at its px 0.
 */
export function cutFrame(
  level: Map<string, Uint8Array>,
  s: ShotShape,
  frame: { px: Uint8Array | Uint8ClampedArray; w: number; h: number; x: number; y: number },
): void {
  const { size } = s;
  // This frame's map px 0, in its own px.
  const [fx, fy] = [Math.round(-frame.x * s.density), Math.round(-frame.y * s.density)];
  for (let j = 0; j < s.ny; j++)
    for (let i = 0; i < s.nx; i++) {
      const key = `${i}/${j}`;
      if (level.has(key)) continue;
      const [x0, y0] = [i * size, j * size];
      const [w, h] = [Math.min(size, s.pw - x0), Math.min(size, s.ph - y0)];
      const [sx, sy] = [fx + x0, fy + y0];
      if (sx < 0 || sy < 0 || sx + w > frame.w || sy + h > frame.h) continue;
      const px = new Uint8Array(size * size * 4);
      for (let y = 0; y < h; y++) {
        const from = ((sy + y) * frame.w + sx) * 4;
        px.set(frame.px.subarray(from, from + w * 4), y * size * 4);
      }
      level.set(key, px);
    }
}

/** A parent tile from its four children (null: nothing there), each 2 × 2 px averaged by their alpha. */
export function halve(children: readonly (Uint8Array | null)[], size: number): Uint8Array | null {
  if (children.every((c) => !c)) return null;
  const out = new Uint8Array(size * size * 4);
  const half = size / 2;
  children.forEach((c, q) => {
    if (!c) return;
    const [ox, oy] = [(q % 2) * half, Math.floor(q / 2) * half];
    for (let y = 0; y < half; y++)
      for (let x = 0; x < half; x++) {
        let [r, g, b, a] = [0, 0, 0, 0];
        for (const [dx, dy] of [
          [0, 0],
          [1, 0],
          [0, 1],
          [1, 1],
        ] as const) {
          const i = ((2 * y + dy) * size + 2 * x + dx) * 4;
          const w = c[i + 3] as number;
          r += (c[i] as number) * w;
          g += (c[i + 1] as number) * w;
          b += (c[i + 2] as number) * w;
          a += w;
        }
        if (a === 0) continue;
        out.set(
          [Math.round(r / a), Math.round(g / a), Math.round(b / a), Math.round(a / 4)],
          ((oy + y) * size + ox + x) * 4,
        );
      }
  });
  return out;
}

/**
 * Every level, the top one (`top`, whole: `cutFrame`'s) first, each the one before halved, down to level 0's one tile.
 * Only the level yielded is held: one taken out of the loop stays whole in memory.
 */
export function* levelsOf(top: Map<string, Uint8Array>, s: ShotShape): Generator<[number, Map<string, Uint8Array>]> {
  let [level, lx, ly] = [top, s.nx, s.ny];
  for (let z = s.levels - 1; ; z--) {
    yield [z, level];
    if (z === 0) return;
    const [ux, uy] = [Math.ceil(lx / 2), Math.ceil(ly / 2)];
    const up = new Map<string, Uint8Array>();
    for (let j = 0; j < uy; j++)
      for (let i = 0; i < ux; i++) {
        const kids = [0, 1, 2, 3].map((q) => level.get(`${2 * i + (q % 2)}/${2 * j + Math.floor(q / 2)}`) ?? null);
        const px = halve(kids, s.size);
        if (px) up.set(`${i}/${j}`, px);
      }
    [level, lx, ly] = [up, ux, uy];
  }
}
